import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthUser } from 'react-auth-kit';
import { Avatar } from '../Components/ui';
import {
    LayoutDashboard, Users, Globe, Settings, Ticket,
    Monitor, ChevronLeft, ChevronRight, ChevronDown, LayoutGrid, Home,
    ListOrdered, CalendarClock, ListChecks, FileText, SlidersHorizontal,
    Building2, ConciergeBell, MessageSquare, UserCog, Store,
    ScanLine, ClipboardList, BarChart3, Contact, FolderOpen, Layers, Gauge,
    MonitorSmartphone, Megaphone
} from 'lucide-react';
import User from '../Models/User';
import datas from '../Assets/data.json';
import { Crypter } from '../Functions/Security/Crypter';
import Logo from '../Assets/EBCallLogo.png';

/** Association route -> icône (décoration visuelle uniquement, le menu vient du backend). */
const routeIcons: { route: string; icon: JSX.Element }[] = [
    { route: '/', icon: <LayoutDashboard /> },
    { route: '/Qm', icon: <Users /> },
    { route: '/pub', icon: <Globe /> },
    { route: '/gestionTicket', icon: <Ticket /> },
    // { route: '/liveScreemPro', icon: <Monitor /> },
    { route: '/liveScreem', icon: <Monitor /> },
    { route: '/setting/manageBorne', icon: <MonitorSmartphone /> },
    { route: '/checkin', icon: <ScanLine /> },
    { route: '/demandes', icon: <ClipboardList /> },
    { route: '/stat', icon: <BarChart3 /> },
    { route: '/gestion', icon: <Home /> },
    { route: '/setting/manageUser', icon: <Users /> },
    { route: '/setting/manageGroup', icon: <UserCog /> },
    { route: '/setting/manageCaisse', icon: <Store /> },
    { route: '/setting/text&message', icon: <MessageSquare /> },
    { route: '/setting/entites', icon: <Building2 /> },
    { route: '/setting/services', icon: <ConciergeBell /> },
    { route: '/setting/priorite', icon: <ListOrdered /> },
    { route: '/setting/creneaux', icon: <CalendarClock /> },
    { route: '/setting/operations', icon: <ListChecks /> },
    { route: '/setting/formulaires', icon: <FileText /> },
    { route: '/setting/clients', icon: <Contact /> },
    { route: '/setting/bi', icon: <Gauge /> },
    { route: '/setting/documents', icon: <FolderOpen /> },
    { route: '/setting/annonces', icon: <Megaphone /> },
    { route: '/setting/parametres', icon: <SlidersHorizontal /> },
    { route: '/setting', icon: <Settings /> },
];

/** Icône de repli pour les groupes sans route (par lettre de code). */
const groupIcons: Record<string, JSX.Element> = {
    B: <Layers />,
    C: <Monitor />,
    D: <Settings />,
};

function iconFor(lien: string, codeHabi?: string): JSX.Element {
    const found = routeIcons.find(r => r.route.toLowerCase() === (lien || '').toLowerCase())
        || routeIcons.find(r => r.route !== '/' && (lien || '').toLowerCase().startsWith(r.route.toLowerCase()));
    if (found) return found.icon;
    if (codeHabi && groupIcons[codeHabi]) return groupIcons[codeHabi];
    return <LayoutGrid />;
}

/** Lit les habilitations chiffrées stockées en local. */
export function getHabilitations(): any[] {
    try {
        return JSON.parse(Crypter.decrypt(localStorage.getItem(datas.Habilitations) + '')) || [];
    } catch {
        return [];
    }
}

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuthUser();
    const user: User = JSON.parse(JSON.stringify(auth() || {}));

    const [collapsed, setCollapsed] = useState(localStorage.getItem('eb_sidebar_collapsed') === '1');
    const [habilitations] = useState<any[]>(getHabilitations());
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
    // Sidebar off-canvas sur mobile (< 960px)
    const [mobileOpen, setMobileOpen] = useState(false);

    const toggle = () => {
        localStorage.setItem('eb_sidebar_collapsed', collapsed ? '0' : '1');
        setCollapsed(!collapsed);
    };

    // Ouverture/fermeture mobile pilotée par le hamburger de la Topbar
    useEffect(() => {
        const onToggle = () => setMobileOpen(o => !o);
        window.addEventListener('eb:toggle-sidebar', onToggle);
        return () => window.removeEventListener('eb:toggle-sidebar', onToggle);
    }, []);

    // Referme le sidebar mobile à chaque navigation
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    // Synchronise la variable CSS --eb-sidebar-w pour le drawer (0px en mobile : sidebar hors flux)
    useEffect(() => {
        const sync = () =>
            document.documentElement.style.setProperty(
                '--eb-sidebar-w',
                window.innerWidth < 960 ? '0px' : (collapsed ? '76px' : '260px')
            );
        sync();
        window.addEventListener('resize', sync);
        return () => window.removeEventListener('resize', sync);
    }, [collapsed]);

    /**
     * Construit l'arborescence depuis les habilitations backend :
     * - racines : codes à 1 caractère ou 'ALL' (A, B, C, D, S…)
     * - enfants : codes multi-caractères rattachés à leur première lettre (B01 -> B)
     * - orphelins : enfants dont le groupe n'est pas retourné -> affichés à la racine
     */
    const { roots, childrenByParent, orphans } = useMemo(() => {
        const roots: any[] = [];
        const childrenByParent: Record<string, any[]> = {};
        const orphans: any[] = [];

        habilitations.forEach((h: any) => {
            const code = h?.CodeHabi;
            if (!code || typeof code !== 'string') return;
            if (code.length === 1 || code === 'ALL') roots.push(h);
            else (childrenByParent[code[0]] ||= []).push(h);
        });

        const rootCodes = new Set(roots.map((r: any) => r.CodeHabi));
        Object.keys(childrenByParent).forEach((p) => {
            if (!rootCodes.has(p)) {
                orphans.push(...childrenByParent[p]);
                delete childrenByParent[p];
            }
        });

        return { roots, childrenByParent, orphans };
    }, [habilitations]);

    const isActive = (lien: string) =>
        !lien ? false
            : lien === '/' ? location.pathname === '/'
                : lien === '/setting' ? (location.pathname === '/setting' || location.pathname === '/setting/')
                    : location.pathname.toLowerCase().startsWith((lien || '').toLowerCase());

    // Déplie automatiquement le groupe contenant la route active
    useEffect(() => {
        const activeChild = habilitations.find(
            (h: any) => h?.CodeHabi?.length > 1 && h.CodeHabi !== 'ALL' && isActive(h.liens)
        );
        if (activeChild) {
            const parent = activeChild.CodeHabi[0];
            setExpandedGroups(prev => (prev[parent] ? prev : { ...prev, [parent]: true }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const toggleGroup = (code: string) =>
        setExpandedGroups(prev => ({ ...prev, [code]: !prev[code] }));

    const renderNavItem = (lien: string, libelle: string, key: string, child = false) => (
        <button
            key={key}
            className={`eb-nav-item${child ? ' eb-nav-sub' : ''}${isActive(lien) ? ' active' : ''}`}
            onClick={() => lien && navigate(lien)}
            title={libelle}
        >
            {iconFor(lien)}
            <span>{libelle}</span>
        </button>
    );

    const renderGroup = (h: any) => {
        const code = h.CodeHabi;
        const kids = childrenByParent[code] || [];
        const expanded = !!expandedGroups[code];
        const groupActive = kids.some((k: any) => isActive(k.liens)) || isActive(h.liens);

        // Racine sans enfants -> simple entrée de menu
        if (kids.length === 0) {
            return renderNavItem(h.liens, h.Libelle, code + h.liens);
        }

        return (
            <div key={code} className="eb-nav-group">
                <button
                    className={`eb-nav-item${groupActive ? ' active-parent' : ''}`}
                    onClick={() => (collapsed ? setCollapsed(false) : toggleGroup(code))}
                    title={h.Libelle}
                >
                    {iconFor(h.liens, code)}
                    <span>{h.Libelle}</span>
                    {!collapsed && (
                        <ChevronDown size={14} className={`eb-nav-chevron${expanded ? ' open' : ''}`} />
                    )}
                </button>
                {collapsed
                    ? kids.map((k: any) => renderNavItem(k.liens, k.Libelle, k.CodeHabi + k.liens))
                    : expanded && (
                        <div className="eb-nav-children">
                            {kids.map((k: any) => renderNavItem(k.liens, k.Libelle, k.CodeHabi + k.liens, true))}
                        </div>
                    )}
            </div>
        );
    };

    return (
        <>
            {/* Overlay mobile — clic = fermeture */}
            {mobileOpen && <div className="eb-sidebar-overlay" onClick={() => setMobileOpen(false)} />}
            <aside className={`eb-sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}>
            <div className="eb-sidebar-header">
                <div className="eb-sidebar-brand" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
                    <img src={Logo} alt="eBCall" />
                    <span>eB·Call</span>
                </div>
                <button className="eb-sidebar-collapse-btn" onClick={toggle} title={collapsed ? 'Déplier' : 'Replier'}>
                    {collapsed ? <ChevronRight /> : <ChevronLeft />}
                </button>
            </div>

            <nav className="eb-sidebar-nav">
                {roots.map((h: any) => renderGroup(h))}
                {orphans.map((h: any) => renderNavItem(h.liens, h.Libelle, h.CodeHabi + h.liens))}
            </nav>

            <div className="eb-sidebar-footer">
                <Avatar name={user?.Name || 'U'} className="flex-shrink-0" />
                <div className="eb-sidebar-footer-info">
                    <div className="name">{user?.Name}</div>
                    <div className="mail">{user?.CodeU} · {user?.Group}</div>
                </div>
            </div>
        </aside>
        </>
    );
};

export default Sidebar;
